import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectField } from '@platform/ui-kit';
import { useDispositionPalette } from '../dispositionPalette';
import { Bubbles, Donut, Timeline, TipRows, monthsAway, useTip, type Tip } from './charts';
import { scaleColor, type Scale } from './scale';
import './ExecutiveDashboard.css';

/**
 * APR Executive Dashboard.
 *
 * NEW WORK, commissioned by the owner — not a port. The source application has no equivalent
 * screen, so §1.2's "clone, do not design" rule has nothing to clone here; the layout below
 * was reviewed as a prototype and built to that.
 *
 * ── WHAT IT ANSWERS ────────────────────────────────────────────────────────────────────
 *
 * Three questions that are genuinely different, which is why they are tabs rather than one
 * long page:
 *
 *   Rationalization   what happens to each application
 *   Cost & Run Rate   where the money goes, and what it becomes
 *   Licensing         what can be renegotiated, and by when
 *
 * ── DISPOSITION COLOUR ─────────────────────────────────────────────────────────────────
 *
 * §1.9.3: colour comes from `apr.scoring_thresholds`, through `dispositionPalette`, and from
 * nowhere else — so editing the Disposition Matrix moves every chart on this screen at once.
 * Nothing here hardcodes a disposition colour.
 *
 * ── SCENARIO ───────────────────────────────────────────────────────────────────────────
 *
 * Savings are a BAND, not a number: `licensing_app_analysis` stores low, mid and high. The
 * case selector picks which, and the realisation slider scales it — because a modelled saving
 * and a banked saving are not the same thing, and the screen should let an executive say so.
 *
 * ── THE RAIL, AND TWO WAYS TO DRAW EVERYTHING (owner-directed, 2026-09-15) ─────────────
 *
 * The scope fields and the KPI cards live in a RAIL that is collapsed to one summary line
 * until the pointer reaches it (or it is pinned, or something inside it has focus). The
 * owner's brief: the selection and the statistics should "disappear or appear" as the mouse
 * comes to that section, so the graphics get the room.
 *
 * Every chart has two forms, switched by ONE control for the whole dashboard: the original
 * bar rows, and circles — packed bubbles sized by value, or a ring for part-to-whole. The
 * bars were not removed (additive change only); circles are the default because that is
 * the look the owner asked for. Renewals additionally has a timeline beside its table.
 *
 * Every mark carries a hover tooltip and, where it names a filter value, a click that
 * cross-filters — a vendor bubble scopes the screen to that vendor, a ring segment to that
 * disposition. The chips, the fields and the marks are one selection.
 *
 * ── WHY ONE FETCH ──────────────────────────────────────────────────────────────────────
 *
 * The endpoint returns one row per application and every aggregate is computed here. Filter
 * clicks re-render from memory rather than round-tripping, which is what makes the
 * cross-filtering feel immediate.
 */

interface AppRow {
  application_id: string;
  application_id_code: string | null;
  name: string | null;
  area: string | null;
  vendor: string | null;
  criticality: string | null;
  users: number | null;
  score: number | null;
  disposition: string | null;
  contract_end: string | null;
  model_id: string | null;
  model_name: string | null;
  total_cost: number | null;
  c_licensing: number | null;
  c_third_party: number | null;
  c_infrastructure: number | null;
  c_indirect: number | null;
  c_cloud: number | null;
  vendor_family: string | null;
  annual_license_cost: number | null;
  savings_low: number | null;
  savings_mid: number | null;
  savings_high: number | null;
  confidence: string | null;
  risk: string | null;
  licensing_disposition: string | null;
  ai_run_disposition: string | null;
  ai_initial_disposition: string | null;
}
/** One disposition value, and which model produced it. */
interface Vocab { name: string; source: string; uses: number }
interface Threshold { decision_name: string; color: string; display_order: number }

type Tab = 'exec' | 'rat' | 'cost' | 'lic';
/** How every chart on the screen is drawn. One switch, remembered per browser. */
type Mode = 'bars' | 'circles';
const MODE_KEY = 'apr.executive-dashboard.mode';
function readMode(): Mode {
  try { return localStorage.getItem(MODE_KEY) === 'bars' ? 'bars' : 'circles'; } catch { return 'circles'; }
}

/** An application with no disposition yet. Selectable like any other value. */
const NOT_SET = 'Not set';
type Case = 'low' | 'mid' | 'high';

const n = (v: number | null | undefined) => (typeof v === 'number' && isFinite(v) ? v : 0);
const money = (v: number) =>
  v >= 1e6 ? `$${(v / 1e6).toFixed(v >= 1e7 ? 0 : 1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${Math.round(v)}`;
const moneyFull = (v: number) => `$${Math.round(v).toLocaleString()}`;
const pctOf = (a: number, b: number) => (b ? `${Math.round((a / b) * 100)}%` : '—');
const sum = <T,>(rows: T[], f: (r: T) => number) => rows.reduce((t, r) => t + f(r), 0);

export default function ExecutiveDashboard() {
  const palette = useDispositionPalette();

  const [rows, setRows] = useState<AppRow[]>([]);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [vocabulary, setVocabulary] = useState<Vocab[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<Tab>('exec');
  const [pickedAreas, setPickedAreas] = useState<string[]>([]);
  const [pickedVendors, setPickedVendors] = useState<string[]>([]);
  const [scenario, setScenario] = useState<Case>('mid');
  const [realise, setRealise] = useState(100);
  /* Disposition is a FILTER with several values, not a single cross-filter click. Nothing
     selected means every disposition, including applications that have none yet. */
  const [pickedDisps, setPickedDisps] = useState<string[]>([]);

  const [mode, setModeState] = useState<Mode>(readMode);
  const setMode = (m: Mode) => {
    setModeState(m);
    try { localStorage.setItem(MODE_KEY, m); } catch { /* private window — the choice just does not persist */ }
  };
  const tip = useTip();

  /*
   * THE PORTFOLIO DEFINITION LAYOUT (owner-directed, 2026-10-23: "use the same design" as
   * /portfolio_definition/executive-dashboard). The scope fields sit in an always-visible
   * toolbar under the heading, as they do there, so the hover-to-open rail of 2026-09-15 and
   * its Pin button are gone; the KPI cards move into the analysis column with a mini-bar
   * each, and a left rail of cards carries the portfolio's health, its dispositions (the
   * cross-filter the chip row used to be), the next renewals and the biggest run rates.
   */
  /*
   * THE HEADING, SCOPE BAR, KPIs AND TABS MUST NOT SCROLL — only the data below them.
   *
   * `height: 100%` does not work: this shell never bounds its content area (`<main>` carries
   * `min-height: 100vh` and then grows with whatever it holds), so a percentage resolves
   * against nothing and the whole window scrolls instead.
   *
   * So the height is MEASURED — window height less this element's top less `<main>`'s bottom
   * padding — and re-measured on resize and whenever `<main>` changes size. A typed constant
   * here would encode another element's size and break on the next state change.
   *
   * A CALLBACK ref, not useRef: this screen mounts after a loading gate, so an effect keyed on
   * a useRef would run once against null and silently never apply a height.
   */
  const [boxH, setBoxH] = useState<number | null>(null);
  const cleanup = useRef<(() => void) | null>(null);
  const rootRef = useCallback((node: HTMLDivElement | null) => {
    cleanup.current?.();
    cleanup.current = null;
    if (!node) return;
    const main = node.closest('main');
    const measure = () => {
      const top = node.getBoundingClientRect().top;
      const padBottom = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      setBoxH(Math.max(360, Math.round(window.innerHeight - top - padBottom)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    window.addEventListener('resize', measure);
    cleanup.current = () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  useEffect(() => () => cleanup.current?.(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch('/api/apr/executive-dashboard/portfolio', { credentials: 'same-origin' });
      const d = (await r.json()) as {
        success: boolean; error?: string;
        applications?: AppRow[]; thresholds?: Threshold[]; vocabulary?: Vocab[];
      };
      if (!d.success) throw new Error(d.error ?? 'unknown');
      setRows(d.applications ?? []);
      setThresholds(d.thresholds ?? []);
      setVocabulary(d.vocabulary ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  /* The disposition vocabulary. The matrix is the authority; anything the data carries that
     the matrix does not name is still shown, appended, rather than silently dropped. */
  /**
   * EVERY disposition in use, from EVERY model — not just the Disposition Matrix.
   *
   * The matrix defines the 4R vocabulary, but the AI licensing analysis has its own
   * (CONSOLIDATE, RENEGOTIATE, RETAIN STRATEGIC …) and an agent run can emit others again.
   * The server returns each value with the model that produced it; the matrix's own values
   * lead, because that is the vocabulary this product is built around.
   */
  const dispositionOptions = useMemo(() => {
    const matrix = thresholds.map((t) => t.decision_name);
    const seen = new Map<string, string[]>();
    vocabulary.forEach((v) => {
      const cur = seen.get(v.name) ?? [];
      if (!cur.includes(v.source)) cur.push(v.source);
      seen.set(v.name, cur);
    });
    const ordered = matrix.concat([...seen.keys()].filter((k) => !matrix.includes(k)).sort());
    const opts = ordered.map((name) => ({
      value: name,
      id: name,
      description: (seen.get(name) ?? ['Disposition Matrix']).join(' · '),
    }));
    if (rows.some((r) => !anyDisposition(r).length)) {
      opts.push({ value: NOT_SET, id: NOT_SET, description: 'no model has ruled on it yet' });
    }
    return opts;
  }, [thresholds, vocabulary, rows]);

  /** The disposition VALUES this application carries, across every model. */
  function anyDisposition(r: AppRow): string[] {
    return [r.disposition, r.licensing_disposition, r.ai_run_disposition, r.ai_initial_disposition]
      .map((x) => (x ?? '').trim())
      .filter(Boolean);
  }

  /** The 4R value used for colour and grouping — the application's own, not the AI's. */
  const dispositions = useMemo(() => {
    const matrix = thresholds.map((t) => t.decision_name);
    const seen = Array.from(new Set(rows.map((r) => (r.disposition ?? '').trim()).filter(Boolean)));
    const all = matrix.concat(seen.filter((x) => !matrix.includes(x)));
    if (rows.some((r) => !(r.disposition ?? '').trim())) all.push(NOT_SET);
    return all;
  }, [thresholds, rows]);

  const areas = useMemo(
    () => Array.from(new Set(rows.map((r) => r.area ?? '').filter(Boolean))).sort(),
    [rows],
  );
  const vendors = useMemo(
    () => Array.from(new Set(rows.map((r) => r.vendor ?? '').filter(Boolean))).sort(),
    [rows],
  );

  /** The saving this application contributes under the chosen case and realisation. */
  const saving = useCallback(
    (r: AppRow) => {
      const base =
        scenario === 'low' ? n(r.savings_low) : scenario === 'high' ? n(r.savings_high) : n(r.savings_mid);
      return base * (realise / 100);
    },
    [scenario, realise],
  );

  const view = useMemo(
    () =>
      rows.filter((r) => {
        if (pickedAreas.length && !pickedAreas.includes(r.area ?? '')) return false;
        if (pickedVendors.length && !pickedVendors.includes(r.vendor ?? '')) return false;
        if (pickedDisps.length) {
          /* Matches against EVERY model's call, so picking "CONSOLIDATE" finds the
             applications the AI licensing analysis said that about — not only the ones whose
             4R disposition happens to use the word. */
          const mine = anyDisposition(r);
          const hit = mine.length ? mine.some((d) => pickedDisps.includes(d))
                                  : pickedDisps.includes(NOT_SET);
          if (!hit) return false;
        }
        return true;
      }),
    [rows, pickedAreas, pickedVendors, pickedDisps],
  );

  const totals = useMemo(() => {
    const cost = sum(view, (r) => n(r.total_cost));
    const lic = sum(view, (r) => n(r.annual_license_cost) || n(r.c_licensing));
    const mid = sum(view, saving);
    const lo = sum(view, (r) => n(r.savings_low) * (realise / 100));
    const hi = sum(view, (r) => n(r.savings_high) * (realise / 100));
    const scored = view.filter((r) => n(r.score) > 0);
    return {
      cost, lic, mid, lo, hi,
      avgScore: scored.length ? sum(scored, (r) => n(r.score)) / scored.length : 0,
      vendors: new Set(view.map((r) => r.vendor ?? '').filter(Boolean)).size,
    };
  }, [view, saving, realise]);

  const byDisposition = useMemo(() => {
    return dispositions
      .map((d) => {
        const rs = view.filter((r) => ((r.disposition ?? '').trim() || NOT_SET) === d);
        return { d, rows: rs, count: rs.length, cost: sum(rs, (r) => n(r.total_cost)), saving: sum(rs, saving) };
      })
      .filter((x) => x.count > 0);
  }, [dispositions, view, saving]);

  const colorOf = (d: string) => palette(d);

  /* A click on a mark is the same selection as the field above it. */
  const flip = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const toggleDisp = (d: string) => setPickedDisps(flip(pickedDisps, d));
  const toggleArea = (a: string) => setPickedAreas(flip(pickedAreas, a));
  const toggleVendor = (v: string) => setPickedVendors(flip(pickedVendors, v));

  if (loading) return <div className="exd2-empty">Loading the portfolio…</div>;
  if (error) return <div className="exd2-err">Could not load the dashboard: {error}</div>;

  const unscoped = !rows.length;

  /* Rail figures. "Dispositioned" is an application the 4R model has ruled on; "licence
     analysed" is one the licensing analysis has produced a savings band for. */
  const dispositioned = view.filter((r) => (r.disposition ?? '').trim()).length;
  const licAnalysed = view.filter((r) => r.savings_mid != null || (r.licensing_disposition ?? '').trim()).length;
  const pctN = (a: number, b: number) => (b ? (a / b) * 100 : 0);
  const gc = (v: number) => (v > 66 ? 'var(--ix-good)' : v > 33 ? 'var(--ix-warn)' : 'var(--ix-crit)');
  const renewing = view.filter((r) => r.contract_end && r.contract_end >= new Date().toISOString().slice(0, 10))
    .sort((a, b) => (a.contract_end ?? '').localeCompare(b.contract_end ?? '')).slice(0, 5);
  const topN = (f: (r: AppRow) => number, k = 8) => view.map(f).sort((a, b) => b - a).slice(0, k);
  const licOf = (r: AppRow) => n(r.annual_license_cost) || n(r.c_licensing);

  return (
    <div className="exd2 ix" ref={rootRef} style={boxH ? { height: boxH } : undefined}>
      <div className="ix-head">
        <div className="ix-titlerow">
          <i className="material-icons ix-titleicon" aria-hidden="true">query_stats</i>
          <div>
            <h1 className="ix-title">Executive Dashboard</h1>
            <p className="ix-sub">Applications, cost and licensing in one view — every figure traceable to the application it came from.</p>
          </div>
          <div className="ix-asof">
            <b>{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</b>
            {`${rows.length} application${rows.length === 1 ? '' : 's'} in the portfolio`}
          </div>
        </div>

        <div className="ix-toolbar">
          <SelectField
            label="Disposition" width="md" multiple allLabel="All dispositions"
            placeholder="— Select a Disposition —"
            value={pickedDisps} onChange={setPickedDisps}
            options={dispositionOptions}
          />
          <SelectField
            label="Business Area" width="md" multiple allLabel="All business areas"
            placeholder="— Select a Business Area —"
            value={pickedAreas} onChange={setPickedAreas}
            options={areas.map((a) => ({ value: a, label: a }))}
          />
          <SelectField
            label="Vendor" width="md" multiple allLabel="All vendors"
            placeholder="— Select a Vendor —"
            value={pickedVendors} onChange={setPickedVendors}
            options={vendors.map((v) => ({ value: v, label: v }))}
          />

          <div className="exd2-field">
            <span>Case</span>
            <div className="exd2-seg">
              {(['low', 'mid', 'high'] as Case[]).map((c) => (
                <button key={c} type="button" aria-pressed={scenario === c} onClick={() => setScenario(c)}>
                  {c === 'low' ? 'Low' : c === 'mid' ? 'Mid' : 'High'}
                </button>
              ))}
            </div>
          </div>

          <div className="exd2-field">
            <span>Realisation</span>
            <div className="exd2-slider">
              <input type="range" min={40} max={100} step={5} value={realise}
                     onChange={(e) => setRealise(+e.target.value)} />
              <span className="exd2-sv">{realise}%</span>
            </div>
          </div>

          <div className="ix-tools">
            <button type="button" className="exd2-reset" onClick={() => {
              setPickedAreas([]); setPickedVendors([]); setPickedDisps([]);
              setScenario('mid'); setRealise(100);
            }}>Reset</button>
            <button type="button" className="exd2-reset" onClick={() => void load()}>↻ Refresh</button>
          </div>
        </div>

        {!unscoped ? (
          <div className="ix-tabs" role="tablist">
            {([['exec', 'Executive Summary'], ['rat', 'Rationalization'],
               ['cost', 'Cost & Run Rate'], ['lic', 'Licensing']] as [Tab, string][]).map(([k, t]) => (
              <button key={k} type="button" role="tab" className="ix-tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t}</button>
            ))}
            {/* ONE switch for how every chart on the screen is drawn. */}
            <div className="exd2-mode" role="group" aria-label="Chart style">
              <button type="button" aria-pressed={mode === 'circles'} onClick={() => setMode('circles')} title="Circles">
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <circle cx="5" cy="8" r="4.5" fill="currentColor" /><circle cx="10.5" cy="4.5" r="3" fill="currentColor" opacity=".55" />
                </svg>
                Circles
              </button>
              <button type="button" aria-pressed={mode === 'bars'} onClick={() => setMode('bars')} title="Bars">
                <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                  <rect x="1" y="2" width="12" height="3" rx="1" fill="currentColor" />
                  <rect x="1" y="7" width="8" height="3" rx="1" fill="currentColor" opacity=".55" />
                </svg>
                Bars
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <div className="ix-body">
        {unscoped ? (
          <div className="exd2-empty">
            <h3>No applications yet</h3>
            <p>
              Add applications on <strong>Portfolio → Applications</strong>, their costs on{' '}
              <strong>Portfolio → Cost</strong>, and run a licensing analysis — this dashboard reads all three.
            </p>
          </div>
        ) : (
          <div className="ix-layout">
            <aside className="ix-rail">
              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Portfolio health</div></div>
                <div className="ix-card-bd">
                  <div className="ix-gauges">
                    <Gauge value={pctN(dispositioned, view.length)} label="Dispositioned" colour={gc(pctN(dispositioned, view.length))} />
                    <Gauge value={pctN(licAnalysed, view.length)} label="Licence analysed" colour={gc(pctN(licAnalysed, view.length))} />
                    <Gauge value={pctN(totals.mid, totals.cost)} label="Saving" colour="var(--ix-accent)" />
                  </div>
                  <div className="ix-ft">
                    <div className="ix-ft-row"><span>Applications</span><b>{view.length}</b></div>
                    <div className="ix-ft-row"><span>Annual run rate</span><b>{money(totals.cost)}</b></div>
                    <div className="ix-ft-row"><span>Addressable savings</span><b>{money(totals.mid)}</b></div>
                    <div className="ix-ft-row"><span>Run rate after action</span><b>{money(totals.cost - totals.mid)}</b></div>
                    <div className="ix-ft-row"><span>Licence spend</span><b>{money(totals.lic)}</b></div>
                  </div>
                </div>
              </div>

              {/* The disposition cross-filter — the chip row that sat above the charts. Its
                  colours are the Disposition Matrix's (§1.9.3), not the blue ramp. */}
              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Disposition</div>
                  <span className="ix-card-meta">{dispositioned} of {view.length} ruled</span>
                </div>
                <div className="ix-card-bd">
                  <div className="ix-stack">
                    {byDisposition.map((x) => (
                      <i key={x.d} style={{ width: `${pctN(x.count, view.length)}%`, background: colorOf(x.d).base }} />
                    ))}
                  </div>
                  <div className="ix-ft" style={{ borderTop: 0, marginTop: 9 }}>
                    {byDisposition.map((x) => {
                      const on = pickedDisps.includes(x.d);
                      return (
                        <button key={x.d} type="button"
                          className={`ix-ft-row ix-ft-btn${pickedDisps.length && !on ? ' dim' : ''}${on ? ' on' : ''}`}
                          aria-pressed={on} title={on ? 'Remove from the selection' : 'Scope the screen to this disposition'}
                          onClick={() => toggleDisp(x.d)}>
                          <span><i className="ix-sw" style={{ background: colorOf(x.d).base, marginRight: 7 }} />{x.d}</span>
                          <b>{x.count}</b>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Next renewals</div></div>
                <div className="ix-card-bd">
                  {renewing.length ? (
                    <div className="ix-ft" style={{ borderTop: 0, marginTop: 0 }}>
                      {renewing.map((r) => (
                        <div className="ix-ft-row" key={r.application_id}
                             onPointerEnter={(e) => tip.show(e, appTip(r, colorOf, saving))} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                          <span className="ix-clip">{r.application_id_code ?? '—'} · {monthsAway(r.contract_end ?? '')}</span>
                          <b>{money(licOf(r))}</b>
                        </div>
                      ))}
                    </div>
                  ) : <p className="ix-note" style={{ margin: 0 }}>No renewal date ahead in this selection.</p>}
                </div>
              </div>

              <div className="ix-card">
                <div className="ix-card-hd"><div className="eyebrow">Biggest run rates</div></div>
                <div className="ix-card-bd">
                  <div className="ix-ft" style={{ borderTop: 0, marginTop: 0 }}>
                    {[...view].sort((a, b) => n(b.total_cost) - n(a.total_cost)).slice(0, 5).map((r) => (
                      <div className="ix-ft-row" key={r.application_id}
                           onPointerEnter={(e) => tip.show(e, appTip(r, colorOf, saving))} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                        <span className="ix-clip">{r.application_id_code ?? '—'} — {r.name}</span>
                        <b>{money(n(r.total_cost))}</b>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <div className="ix-main">
              <div className="ix-kpis">
                <Kpi label="Applications" value={String(view.length)}
                     sub={`${totals.vendors} vendor${totals.vendors === 1 ? '' : 's'}`}
                     graphic={<MiniBars values={byDisposition.map((x) => x.count)} colour="var(--ix-accent)" />} />
                <Kpi label="Annual run rate" value={money(totals.cost)} sub={moneyFull(totals.cost)}
                     graphic={<MiniBars values={topN((r) => n(r.total_cost))} colour="var(--ix-s3)" />} />
                <Kpi label="Addressable savings" value={money(totals.mid)}
                     delta={`${pctOf(totals.mid, totals.cost)} of run rate`} deltaCol="var(--ix-good)"
                     sub={`${money(totals.lo)} – ${money(totals.hi)}`}
                     graphic={<MiniBars values={topN(saving)} colour="var(--ix-good)" />} />
                <Kpi label="Run rate after action" value={money(totals.cost - totals.mid)}
                     sub={`${pctOf(totals.mid, totals.cost)} reduction`}
                     graphic={<MiniBars values={topN((r) => Math.max(0, n(r.total_cost) - saving(r)))} colour="var(--ix-s3)" />} />
                <Kpi label="Licence spend" value={money(totals.lic)}
                     sub={`${pctOf(totals.lic, totals.cost)} of run rate`}
                     graphic={<MiniBars values={topN(licOf)} colour="var(--ix-accent)" />} />
                <Kpi label="Average score" value={totals.avgScore ? totals.avgScore.toFixed(0) : '—'}
                     sub="across scored applications"
                     graphic={<MiniBars values={topN((r) => n(r.score))} colour="var(--ix-s2)" />} />
              </div>

              {tab === 'exec' && <ExecTab view={view} totals={totals} byDisposition={byDisposition} colorOf={colorOf} saving={saving} scenario={scenario}
                                           mode={mode} tip={tip} picked={pickedDisps} toggleDisp={toggleDisp} scaleColor={scaleColor} />}
              {tab === 'rat'  && <RatTab view={view} areas={areas} byDisposition={byDisposition} colorOf={colorOf} saving={saving}
                                          mode={mode} tip={tip} pickedAreas={pickedAreas} toggleArea={toggleArea} scaleColor={scaleColor} />}
              {tab === 'cost' && <CostTab view={view} totals={totals} byDisposition={byDisposition} colorOf={colorOf}
                                           mode={mode} tip={tip} picked={pickedDisps} toggleDisp={toggleDisp} scaleColor={scaleColor} />}
              {tab === 'lic'  && <LicTab view={view} colorOf={colorOf} saving={saving}
                                          mode={mode} tip={tip} pickedVendors={pickedVendors} toggleVendor={toggleVendor} scaleColor={scaleColor} />}
            </div>
          </div>
        )}
      </div>
      {tip.node}
    </div>
  );
}

/* ── pieces ────────────────────────────────────────────────────────────────────────── */

/* Gauge and MiniBars are COPIED from pages/portfolio_definition/executive/ExecutiveDashboard.tsx
   (Gauge, MiniBars) — products do not import from one another's folders (§1.7). */
function Gauge({ value, label, colour }: { value: number; label: string; colour: string }) {
  const size = 62, r = size / 2 - 7, c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className="ix-gauge">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={`${label} ${Math.round(v)}%`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5eaef" strokeWidth={7} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colour} strokeWidth={7}
          strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        <text x={size / 2} y={size / 2 + 4} textAnchor="middle"
          style={{ font: "700 12.5px 'IBM Plex Mono',monospace", fill: '#14202a' }}>{Math.round(v)}%</text>
      </svg>
      <span className="lbl">{label}</span>
    </div>
  );
}

function MiniBars({ values, colour }: { values: number[]; colour: string }) {
  if (!values.length || !values.some((v) => v > 0)) return null;
  const W = 118, H = 26, gap = 3;
  const bw = (W - gap * (values.length - 1)) / values.length;
  const max = Math.max(...values) || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-hidden="true">
      {values.map((v, i) => {
        const h = Math.max(v > 0 ? 2 : 0, (v / max) * (H - 3));
        return <rect key={i} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={1.5}
          fill={colour} fillOpacity={v > 0 ? 0.9 : 0.18} />;
      })}
    </svg>
  );
}

function Kpi({ label, value, delta, deltaCol, sub, graphic }: {
  label: string; value: string; delta?: string; deltaCol?: string; sub: string; graphic?: React.ReactNode;
}) {
  return (
    <div className="ix-kpi">
      <div className="eyebrow">{label}</div>
      <div className="v"><b>{value}</b>{delta ? <span className="d" style={{ color: deltaCol }}>{delta}</span> : null}</div>
      {graphic ? <div className="ix-spark">{graphic}</div> : null}
      <small>{sub}</small>
    </div>
  );
}

function Card({ eyebrow, title, meta, sub, span, action, children }: {
  eyebrow: string; title: string; meta?: string; sub?: string; span?: boolean; action?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className={`ix-panel${span ? ' span' : ''}`}>
      <header>
        <div className="ix-panel-top">
          <div className="eyebrow">{eyebrow}</div>
          <span className="c-right">
            {meta ? <span className="c-meta">{meta}</span> : null}
            {action}
          </span>
        </div>
        <h2>{title}</h2>
        {sub ? <p className="ix-note">{sub}</p> : null}
      </header>
      {children}
    </section>
  );
}

type Colored = (d: string) => { base: string; ink: string; tint: string; onBase: string };
type Bucket = { d: string; rows: AppRow[]; count: number; cost: number; saving: number };

/** A little Table | Chart style switch that lives in a card's header. */
function CardSwitch<T extends string>({ value, onChange, options }: {
  value: T; onChange: (v: T) => void; options: [T, string][];
}) {
  return (
    <div className="exd2-cswitch" role="group">
      {options.map(([k, l]) => (
        <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}>{l}</button>
      ))}
    </div>
  );
}

/**
 * A disposition named inside a tooltip: a FILLED chip in its Disposition Matrix colour
 * (§1.9.3 rule 2), with black or white text by luminance. Owner-directed 2026-10-25.
 */
function dispChip(d: string, colorOf: Colored) {
  const c = colorOf(d);
  return <span className="t-disp" style={{ background: c.base, color: c.onBase }}>{d}</span>;
}

/** The tooltip for one application, used by every mark that stands for one. */
function appTip(r: AppRow, colorOf: Colored, saving: (r: AppRow) => number) {
  const c = colorOf(r.disposition ?? '');
  const ai = [r.licensing_disposition, r.ai_run_disposition, r.ai_initial_disposition]
    .map((x) => (x ?? '').trim()).filter(Boolean);
  return (
    <TipRows
      swatch={c.base}
      title={<><span className="mono">{r.application_id_code ?? '—'}</span> — {r.name}</>}
      sub={[r.vendor, r.area, r.users ? `${r.users.toLocaleString()} users` : null].filter(Boolean).join(' · ')}
      rows={[
        ['disposition', dispChip(r.disposition || NOT_SET, colorOf)],
        ...(ai.length ? [['AI says', Array.from(new Set(ai)).join(' · ')] as [string, React.ReactNode]] : []),
        ['run rate', moneyFull(n(r.total_cost))],
        ['saving', moneyFull(saving(r))],
        ...(r.contract_end ? [['renews', `${r.contract_end} · ${monthsAway(r.contract_end)}`] as [string, React.ReactNode]] : []),
      ]}
    />
  );
}

function ExecTab({ view, totals, byDisposition, colorOf, saving, scenario, mode, tip, picked, toggleDisp, scaleColor }: {
  view: AppRow[]; totals: { cost: number; mid: number }; byDisposition: Bucket[];
  colorOf: Colored; saving: (r: AppRow) => number; scenario: Case;
  mode: Mode; tip: Tip; picked: string[]; toggleDisp: (d: string) => void; scaleColor: Scale;
}) {
  const top = [...view].sort((a, b) => saving(b) - saving(a)).slice(0, 6);
  const maxS = Math.max(1, ...top.map(saving));
  const dispTip = (x: Bucket) => (
    <TipRows title={dispChip(x.d, colorOf)}
             rows={[['applications', x.count], ['run rate', moneyFull(x.cost)],
                    ['share of run rate', pctOf(x.cost, totals.cost)], ['saving', moneyFull(x.saving)]]} />
  );
  return (
    <div className="exd2-grid">
      <Card eyebrow="Summary" title="The position" span>
        <p className="exd2-lede">
          The portfolio in scope runs <b>{moneyFull(totals.cost)}</b> a year across{' '}
          <b>{view.length}</b> applications. On the {scenario} case a programme releases{' '}
          <b>{moneyFull(totals.mid)}</b> — <b>{pctOf(totals.mid, totals.cost)}</b> of run rate.
        </p>
      </Card>

      <Card eyebrow="Where the money is" title="Portfolio mix" meta="share of run rate" span
            sub="How the money divides across dispositions — not how the applications divide.">
        {mode === 'bars' ? (
          <div className="exd2-mix">
            {byDisposition.map((x) => {
              const c = colorOf(x.d);
              const w = totals.cost ? (x.cost / totals.cost) * 100 : 0;
              if (w <= 0) return null;
              return (
                <div key={x.d} className="exd2-mix-seg" style={{ width: `${w}%`, background: c.base, color: c.onBase }}
                     onPointerEnter={(e) => tip.show(e, dispTip(x))} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                  {w > 9 ? `${Math.round(w)}%` : ''}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="exd2-ring-row">
            <Donut size={228} tip={tip}
                   centre={{ top: money(totals.cost), bot: `${view.length} applications` }}
                   parts={byDisposition.map((x) => ({
                     key: x.d, value: x.cost, fill: colorOf(x.d).base, tip: dispTip(x),
                     onClick: () => toggleDisp(x.d),
                     selected: picked.includes(x.d), dimmed: picked.length > 0 && !picked.includes(x.d),
                   }))} />
            <div className="exd2-key">
              {byDisposition.map((x) => {
                const c = colorOf(x.d);
                return (
                  <button key={x.d} type="button" className={`exd2-key-row${picked.length && !picked.includes(x.d) ? ' dim' : ''}`}
                          aria-pressed={picked.includes(x.d)} onClick={() => { tip.hide(); toggleDisp(x.d); }}
                          onPointerEnter={(e) => tip.show(e, dispTip(x))} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                    <span className="dot" style={{ background: c.base }} />
                    <span className="k-name">{x.d}</span>
                    <span className="k-val">{money(x.cost)}</span>
                    <span className="k-pct">{pctOf(x.cost, totals.cost)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      <Card eyebrow="Biggest moves" title="The moves that matter" meta="by saving" span
            sub="The applications carrying the most releasable cost under the chosen case.">
        {mode === 'bars' ? (
          <div className="exd2-moves">
            {top.map((r, i) => {
              const c = colorOf(r.disposition ?? '');
              const sc = scaleColor(saving(r), maxS);
              return (
                <div key={r.application_id} className="exd2-move" style={{ borderLeft: `5px solid ${sc.fill}` }}>
                  <div className="rank" style={{ color: sc.ink }}>{i + 1}</div>
                  <div>
                    <div className="m-ti">
                      <span className="mono">{r.application_id_code ?? '—'}</span> — {r.name}{' '}
                      <span className="exd2-pill" style={{ background: c.tint, color: c.ink }}>
                        {r.disposition || 'Not set'}
                      </span>
                    </div>
                    <div className="m-bd">
                      {[r.vendor, r.area, r.users ? `${r.users.toLocaleString()} users` : null,
                        r.contract_end ? `renews ${r.contract_end}` : null]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="m-val">{money(saving(r))}<small>saving</small></div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="exd2-ring-row">
            <Bubbles tip={tip} height={300}
                     items={top.map((r) => {
                       const sc = scaleColor(saving(r), maxS);
                       return {
                         key: r.application_id, label: r.application_id_code ?? r.name ?? '—',
                         sub: money(saving(r)), value: saving(r), fill: sc.fill, ink: sc.ink,
                         tip: appTip(r, colorOf, saving),
                       };
                     })} />
            <div className="exd2-key">
              {top.map((r, i) => {
                const c = { base: scaleColor(saving(r), maxS).fill };
                return (
                  <div key={r.application_id} className="exd2-key-row"
                       onPointerEnter={(e) => tip.show(e, appTip(r, colorOf, saving))} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                    <span className="rank">{i + 1}</span>
                    <span className="dot" style={{ background: c.base }} />
                    <span className="k-name"><span className="mono">{r.application_id_code ?? '—'}</span> — {r.name}</span>
                    <span className="k-val">{money(saving(r))}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

function RatTab({ view, areas, byDisposition, colorOf, saving, mode, tip, pickedAreas, toggleArea, scaleColor }: {
  view: AppRow[]; areas: string[]; byDisposition: Bucket[]; colorOf: Colored; saving: (r: AppRow) => number;
  mode: Mode; tip: Tip; pickedAreas: string[]; toggleArea: (a: string) => void; scaleColor: Scale;
}) {
  const live = areas.filter((a) => view.some((r) => r.area === a));
  const maxN = Math.max(1, ...live.map((a) => view.filter((r) => r.area === a).length));
  const areaTipOf = (area: string, rs: AppRow[]) => (
    <TipRows title={area} swatch={scaleColor(rs.length, maxN).fill}
             sub={`${rs.length} application${rs.length === 1 ? '' : 's'} · ${moneyFull(sum(rs, (r) => n(r.total_cost)))} run rate`}
             rows={byDisposition.map((x) => {
               const nn = rs.filter((r) => ((r.disposition ?? '').trim() || NOT_SET) === x.d).length;
               return [dispChip(x.d, colorOf), nn] as [React.ReactNode, React.ReactNode];
             }).filter((row) => (row[1] as number) > 0)} />
  );
  return (
    <div className="exd2-grid">
      <Card eyebrow="Rationalization" title="Disposition by business area" meta={`${view.length} applications`} span
            sub={mode === 'bars' ? 'Bar length is the number of applications in that area. Click an area to scope to it.'
                                 : 'One circle per area, sized by its application count. Click an area to scope to it.'}>
        {mode === 'bars' ? (
          <div className="exd2-rows">
            {live.map((area) => {
              const rs = view.filter((r) => r.area === area);
              const sc = scaleColor(rs.length, maxN);
              const on = pickedAreas.includes(area);
              return (
                <div className={`exd2-row click${pickedAreas.length && !on ? ' dim' : ''}`} key={area}
                     onClick={() => { tip.hide(); toggleArea(area); }}
                     onPointerEnter={(e) => tip.show(e, areaTipOf(area, rs))} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                  <span className="r-lab">{area}</span>
                  <div className="r-bar"><div style={{ width: `${(rs.length / maxN) * 100}%`, background: sc.fill }} /></div>
                  <span className="r-num">{rs.length}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="exd2-rings">
            {live.map((area) => {
              const rs = view.filter((r) => r.area === area);
              const size = Math.round(84 + Math.sqrt(rs.length / maxN) * 96);
              const sc = scaleColor(rs.length, maxN);
              const on = pickedAreas.includes(area);
              const dim = pickedAreas.length > 0 && !on;
              const areaTip = areaTipOf(area, rs);
              return (
                <button type="button" key={area} className={`exd2-ring-cell${on ? ' sel' : ''}${dim ? ' dim' : ''}`}
                        aria-pressed={on} onClick={() => { tip.hide(); toggleArea(area); }}
                        onPointerEnter={(e) => tip.show(e, areaTip)} onPointerMove={tip.move} onPointerLeave={tip.hide}>
                  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="exd2-disc">
                    <circle cx={size / 2} cy={size / 2} r={size / 2 - 2} fill={sc.fill} />
                    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle"
                          fontSize={size * 0.22} fontWeight={600} fill={sc.ink}>{rs.length}</text>
                  </svg>
                  <span className="rc-lab">{area}</span>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      <Card eyebrow="Register" title="Applications" meta={`${view.length} rows`} span>
        <div className="exd2-tbl">
          <table>
            <thead>
              <tr>
                <th>Application</th><th>Area</th><th>Vendor</th>
                <th className="num">Users</th><th className="num">Score</th>
                <th>Disposition</th><th className="num">Run rate</th><th className="num">Saving</th>
              </tr>
            </thead>
            <tbody>
              {[...view].sort((a, b) => n(b.total_cost) - n(a.total_cost)).map((r) => {
                const c = colorOf(r.disposition ?? '');
                return (
                  <tr key={r.application_id}>
                    {/* The ID leads: it is what is searched for and quoted, and the
                        description is the qualifier after it. */}
                    <td>
                      <b className="mono">{r.application_id_code ?? '—'}</b>
                      <br />
                      <span className="muted">{r.name}</span>
                    </td>
                    <td>{r.area}</td><td>{r.vendor}</td>
                    <td className="num">{r.users ? r.users.toLocaleString() : '—'}</td>
                    <td className="num">{r.score ?? '—'}</td>
                    <td><span className="exd2-pill" style={{ background: c.tint, color: c.ink }}>
                      {r.disposition || 'Not set'}</span></td>
                    <td className="num">{moneyFull(n(r.total_cost))}</td>
                    <td className="num">{moneyFull(saving(r))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function CostTab({ view, totals, byDisposition, colorOf, mode, tip, picked, toggleDisp, scaleColor }: {
  view: AppRow[]; totals: { cost: number; mid: number }; byDisposition: Bucket[]; colorOf: Colored;
  mode: Mode; tip: Tip; picked: string[]; toggleDisp: (d: string) => void; scaleColor: Scale;
}) {
  const comp: [string, number][] = [
    ['Software licensing', sum(view, (r) => n(r.c_licensing))],
    ['Cloud / SaaS', sum(view, (r) => n(r.c_cloud))],
    ['Infrastructure', sum(view, (r) => n(r.c_infrastructure))],
    ['Third-party support', sum(view, (r) => n(r.c_third_party))],
    ['Indirect operations', sum(view, (r) => n(r.c_indirect))],
  ];
  const maxC = Math.max(1, ...comp.map((c) => c[1]));
  const top = [...view].sort((a, b) => n(b.total_cost) - n(a.total_cost)).slice(0, 12);
  const maxT = Math.max(1, ...top.map((t) => n(t.total_cost)));
  const noSave = (r: AppRow) => n(r.savings_mid) * 0; // the cost tab does not show a saving
  return (
    <div className="exd2-grid">
      <Card eyebrow="Cost components" title="Where the run rate goes" meta={`${money(totals.cost)} total`} span={mode === 'bars'}
            sub="The five cost components carried on every application.">
        {mode === 'bars' ? (
          <div className="exd2-rows">
            {comp.filter((c) => c[1] > 0).map(([label, v]) => (
              <div className="exd2-row" key={label}>
                <span className="r-lab">{label}</span>
                <div className="r-bar"><div style={{ width: `${(v / maxC) * 100}%`, background: scaleColor(v, maxC).fill }} /></div>
                <span className="r-num">{money(v)} · {pctOf(v, totals.cost)}</span>
              </div>
            ))}
          </div>
        ) : (
          <Bubbles tip={tip} height={290}
                   items={comp.map(([label, v]) => ({
                     key: label, label, sub: money(v), value: v, fill: scaleColor(v, maxC).fill, ink: scaleColor(v, maxC).ink,
                     tip: <TipRows title={label} rows={[['a year', moneyFull(v)], ['of run rate', pctOf(v, totals.cost)]]} />,
                   }))} />
        )}
      </Card>

      <Card eyebrow="Savings" title="Savings by disposition" meta="chosen case" span={mode === 'bars'}
            sub="Which decisions release the money.">
        {mode === 'bars' ? (
          <div className="exd2-rows">
            {byDisposition.filter((x) => x.saving > 0)
              .sort((a, b) => b.saving - a.saving).map((x) => {
              const mx = Math.max(1, ...byDisposition.map((y) => y.saving));
              const c = scaleColor(x.saving, mx);
              return (
                <div className="exd2-row" key={x.d}>
                  <span className="r-lab">{x.d}</span>
                  <div className="r-bar"><div style={{ width: `${(x.saving / mx) * 100}%`, background: c.fill }} /></div>
                  <span className="r-num">{money(x.saving)}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <Bubbles tip={tip} height={290}
                   items={byDisposition.map((x) => {
                     const mx = Math.max(1, ...byDisposition.map((y) => y.saving));
                     const c = scaleColor(x.saving, mx);
                     return {
                       key: x.d, label: x.d, sub: money(x.saving), value: x.saving, fill: c.fill, ink: c.ink,
                       tip: <TipRows title={dispChip(x.d, colorOf)}
                                     rows={[['saving', moneyFull(x.saving)], ['applications', x.count], ['run rate', moneyFull(x.cost)]]} />,
                       onClick: () => toggleDisp(x.d),
                       selected: picked.includes(x.d), dimmed: picked.length > 0 && !picked.includes(x.d),
                     };
                   })} />
        )}
      </Card>

      <Card eyebrow="Concentration" title="The twelve most expensive applications" meta="coloured by run rate" span
            sub="Concentration matters more than the count.">
        {mode === 'bars' ? (
          <div className="exd2-rows">
            {top.map((r) => {
              const sc = scaleColor(n(r.total_cost), maxT);
              return (
                <div className="exd2-row" key={r.application_id}>
                  <span className="r-lab" title={r.name ?? ''}>
                    {r.application_id_code ?? '—'} — {r.name}
                  </span>
                  <div className="r-bar"><div style={{ width: `${(n(r.total_cost) / maxT) * 100}%`, background: sc.fill }} /></div>
                  <span className="r-num">{moneyFull(n(r.total_cost))}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <Bubbles tip={tip} height={360}
                   items={top.map((r) => {
                     const sc = scaleColor(n(r.total_cost), maxT);
                     return {
                       key: r.application_id, label: r.application_id_code ?? r.name ?? '—',
                       sub: money(n(r.total_cost)), value: n(r.total_cost), fill: sc.fill, ink: sc.ink,
                       tip: appTip(r, colorOf, noSave),
                     };
                   })} />
        )}
      </Card>
    </div>
  );
}

function LicTab({ view, colorOf, saving, mode, tip, pickedVendors, toggleVendor, scaleColor }: {
  view: AppRow[]; colorOf: Colored; saving: (r: AppRow) => number;
  mode: Mode; tip: Tip; pickedVendors: string[]; toggleVendor: (v: string) => void; scaleColor: Scale;
}) {
  const [renew, setRenew] = useState<'timeline' | 'table'>('timeline');
  const byVendor = useMemo(() => {
    const m = new Map<string, { lic: number; count: number; save: number }>();
    view.forEach((r) => {
      const v = r.vendor || '—';
      const cur = m.get(v) ?? { lic: 0, count: 0, save: 0 };
      cur.lic += n(r.annual_license_cost) || n(r.c_licensing);
      cur.count += 1;
      cur.save += saving(r);
      m.set(v, cur);
    });
    return [...m.entries()].map(([v, d]) => ({ v, ...d })).sort((a, b) => b.lic - a.lic).slice(0, 12);
  }, [view, saving]);
  const maxL = Math.max(1, ...byVendor.map((v) => v.lic));
  const totalL = sum(byVendor, (v) => v.lic);
  const withContract = view.filter((r) => r.contract_end)
    .sort((a, b) => (a.contract_end ?? '').localeCompare(b.contract_end ?? ''));
  const licOf = (r: AppRow) => n(r.annual_license_cost) || n(r.c_licensing);
  const maxLic = Math.max(1, ...withContract.map(licOf));
  return (
    <div className="exd2-grid">
      <Card eyebrow="Leverage" title="Vendor concentration" meta="licence spend" span
            sub={mode === 'bars' ? 'Negotiating leverage follows concentration.'
                                 : 'Negotiating leverage follows concentration. Circle area is licence spend; click a vendor to scope the screen to it.'}>
        {mode === 'bars' ? (
          <div className="exd2-rows">
            {byVendor.map((x) => (
              <div className="exd2-row" key={x.v}>
                <span className="r-lab">{x.v}</span>
                <div className="r-bar"><div style={{ width: `${(x.lic / maxL) * 100}%`, background: scaleColor(x.lic, maxL).fill }} /></div>
                <span className="r-num">{money(x.lic)} · {x.count} app{x.count === 1 ? '' : 's'}</span>
              </div>
            ))}
          </div>
        ) : (
          <Bubbles tip={tip} height={340}
                   items={byVendor.map((x) => {
                     const on = pickedVendors.includes(x.v);
                     const sc = scaleColor(x.lic, maxL);
                     return {
                       key: x.v, label: x.v, sub: money(x.lic), value: x.lic,
                       fill: sc.fill, ink: sc.ink,
                       tip: <TipRows title={x.v}
                                     rows={[['licence a year', moneyFull(x.lic)], ['of licence spend', pctOf(x.lic, totalL)],
                                            ['applications', x.count], ['saving', moneyFull(x.save)]]} />,
                       onClick: () => toggleVendor(x.v),
                       selected: on, dimmed: pickedVendors.length > 0 && !on,
                     };
                   })} />
        )}
      </Card>

      <Card eyebrow="Timing" title="Renewals" meta={`${withContract.length} with a date`} span
            sub="Every renewal is a negotiation window, and they do not wait."
            action={<CardSwitch value={renew} onChange={setRenew} options={[['timeline', 'Timeline'], ['table', 'Table']]} />}>
        {renew === 'table' ? (
          <div className="exd2-tbl">
            <table>
              <thead>
                <tr><th>Renews</th><th>Application</th><th>Vendor</th><th>Family</th>
                    <th className="num">Licence</th><th className="num">Saving</th>
                    <th>Confidence</th><th>Risk</th></tr>
              </thead>
              <tbody>
                {withContract.map((r) => {
                  const c = colorOf(r.disposition ?? '');
                  return (
                    <tr key={r.application_id}>
                      <td className="mono">{r.contract_end}</td>
                      <td><b className="mono">{r.application_id_code ?? '—'}</b> — {r.name}{' '}
                        <span className="exd2-pill" style={{ background: c.tint, color: c.ink }}>
                          {r.disposition || 'Not set'}</span></td>
                      <td>{r.vendor}</td><td>{r.vendor_family ?? '—'}</td>
                      <td className="num">{moneyFull(n(r.annual_license_cost) || n(r.c_licensing))}</td>
                      <td className="num">{moneyFull(saving(r))}</td>
                      <td>{r.confidence ?? '—'}</td><td>{r.risk ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <Timeline tip={tip}
                      items={withContract.map((r) => {
                        const c = colorOf(r.disposition ?? '');
                        const lic = licOf(r);
                        return {
                          key: r.application_id, date: r.contract_end ?? '', value: lic, fill: scaleColor(lic, maxLic).fill,
                          label: r.application_id_code ?? r.name ?? '—',
                          tip: (
                            <TipRows swatch={c.base}
                                     title={<><span className="mono">{r.application_id_code ?? '—'}</span> — {r.name}</>}
                                     sub={[r.vendor, r.vendor_family ? `${r.vendor_family} family` : null].filter(Boolean).join(' · ')}
                                     rows={[
                                       ['renews', `${r.contract_end} · ${monthsAway(r.contract_end ?? '')}`],
                                       ['licence a year', moneyFull(lic)],
                                       ['saving', moneyFull(saving(r))],
                                       ['disposition', dispChip(r.disposition || NOT_SET, colorOf)],
                                       ...(r.licensing_disposition ? [['AI says', r.licensing_disposition] as [string, React.ReactNode]] : []),
                                       ['confidence · risk', `${r.confidence ?? '—'} · ${r.risk ?? '—'}`],
                                     ]} />
                          ),
                        };
                      })} />
            <p className="exd2-tl-note">Circle area and colour are licence spend — deeper is larger. Hover a circle for the application; the strip under the axis counts renewals per month.</p>
          </>
        )}
      </Card>
    </div>
  );
}
